export type HomeAIErrorCode = 'SOMETHING_WENT_WRONG' | 'NETWORK_ERROR' | 'RATE_LIMITED'

export interface HomeAIMessages {
    greeting: string
    placeholder: string
    animatedPlaceholders: string[]
    searchAriaLabel: string
    preferencesLabel: string
    confirmSearch: string,
    description: string
    /** Shown under the main traveller line: counts without age bands (matches recent-search style). */
    pendingTripPassengers: {
        adultsOne: string
        adultsMany: string
        childOne: string
        childrenMany: string
        infantOne: string
        infantsMany: string
        joiner: string
    }
    /** Between passenger breakdown and AI preference text on the details row (e.g. " · · · | Morning flights"). */
    pendingTripPreferenceSeparator: string
    errors: Record<HomeAIErrorCode, string>
    fallbacks: {
        clarificationDefault: string
        reviewTripPrompt: string
        reviewTripQuickReplies: string[]
    }
    /** Strings used by the API route for normalization and fallbacks (shared en/ar). */
    api: {
        askAboutFlights: string
        askAboutFlightsWithPending: string
        greetingReply: string
        unrelatedReply: string
        tellRouteAndDate: string
        shareAirportsAndDate: string
        shareMoreTravellers: string
        returnDateBeforeDepart: string
        multiCityNotSupported: string
        questions: {
            from: string
            to: string
            passengers: string
            departDate: string
            returnDate: string
        }
        passengerValidation: {
            adultsRange: string
            noSeatsForChildren: string
            /** When maxChildren is positive but requested children count is invalid. */
            childrenAsk: (maxChildren: number, adults: number) => string
            infantsExceedAdults: string
        }
    }
}

export const homeAIMessages: Record<'en', HomeAIMessages> & Record<'ar', HomeAIMessages> = {
    en: {
        greeting: 'Hi! Where are you planning to go?',
        placeholder: 'Ask AI to find flights for your trip',
        animatedPlaceholders: [
            'Find me a morning non-stop flight from Bengaluru to Dubai tomorrow',
            'Book the cheapest evening flight from Delhi to Bangkok next Friday',
            'Show direct flights from Mumbai to Singapore for 2 adults and 1 child',
            'I want a business class flight from Riyadh to London next month',
            'Find me flights with included checked baggage from Chennai to Abu Dhabi'
        ],
        searchAriaLabel: 'Search with AI',
        preferencesLabel: 'Preferences',
        confirmSearch: 'Confirm and search',
        description: 'AI may make mistakes. Please verify flight, fare, baggage, and traveller details before booking.',
        pendingTripPassengers: {
            adultsOne: '1 adult',
            adultsMany: '{count} adults',
            childOne: '1 child',
            childrenMany: '{count} children',
            infantOne: '1 infant',
            infantsMany: '{count} infants',
            joiner: ' · '
        },
        pendingTripPreferenceSeparator: ' | ',
        errors: {
            SOMETHING_WENT_WRONG: 'Something went wrong. Please try again.',
            NETWORK_ERROR: 'Unable to connect. Please check your connection and try again.',
            RATE_LIMITED: 'Too many requests. Please wait a moment and try again.'
        },
        fallbacks: {
            clarificationDefault: 'Please share the missing trip details.',
            reviewTripPrompt:
                'Please review your trip details below — would you like to make any changes, or shall I continue?',
            reviewTripQuickReplies: ['Let\'s go', 'OK', 'Continue', 'Looks good', 'Proceed']
        },
        api: {
            askAboutFlights: 'I can help you find your preferred flights.',
            askAboutFlightsWithPending: 'I can only help with flights. ',
            greetingReply: 'Hi! I can help you search flights and update trip details.',
            unrelatedReply: 'Sorry, I cannot help with that here. I can help with flight search details.',
            tellRouteAndDate: 'Tell me your route and date, for example: Bengaluru to Dubai on 15 April.',
            shareAirportsAndDate: 'Please share departure airport, destination airport, and departure date.',
            shareMoreTravellers: 'Could you share a bit more so we can set travellers correctly?',
            returnDateBeforeDepart: 'Return date cannot be before departure date. Please share a valid return date.',
            multiCityNotSupported:
                'Multi-city trips are not supported yet. I can search one-way or round-trip flights — please share a single origin and destination.',
            questions: {
                from: 'What is your departure city or airport?',
                to: 'What is your destination city or airport?',
                passengers:
                    'How many travellers? Adults are 11+; children 2–11; infants under 2 (each infant needs one adult).',
                departDate: 'What is your departure date? (for example 15 April)',
                returnDate: 'What is your return date? (for example 22 April)'
            },
            passengerValidation: {
                adultsRange: `How many adults are travelling? Choose 1–9 (each adult must be 11 years or older).`,
                noSeatsForChildren:
                    'With nine adults, no more seats are left for children on this booking. How many adults and children should we use?',
                childrenAsk: (maxChildren: number, adults: number) =>
                    `How many children aged 2–11? You can add up to ${maxChildren} child${maxChildren === 1 ? '' : 'ren'} with ${adults} adult${adults === 1 ? '' : 's'}.`,
                infantsExceedAdults:
                    'How many infants (under 2 years)? Infants cannot exceed the number of adults (each infant must travel with one adult).'
            }
        }
    },
    ar: {
        greeting: 'مرحبًا! إلى أين تخطط للسفر؟',
        placeholder: 'اطلب من الذكاء الاصطناعي إيجاد رحلات لرحلتك',
        animatedPlaceholders: [
            'اعثر لي على رحلة صباحية مباشرة من بنغالورو إلى دبي غدًا',
            'احجز أرخص رحلة مسائية من دلهي إلى بانكوك يوم الجمعة القادم',
            'اعرض رحلات مباشرة من مومباي إلى سنغافورة لشخصين بالغين وطفل واحد',
            'أريد رحلة درجة رجال الأعمال من الرياض إلى لندن الشهر القادم',
            'اعثر لي على رحلات تشمل أمتعة مسجلة من تشيناي إلى أبوظبي'
        ],
        searchAriaLabel: 'البحث بالذكاء الاصطناعي',
        preferencesLabel: 'التفضيلات',
        confirmSearch: 'تأكيد والبحث',
        description: 'قد يخطئ الذكاء الاصطناعي. يرجى التحقق من تفاصيل الرحلة والسعر والأمتعة وبيانات المسافرين قبل الحجز.',
        pendingTripPassengers: {
            adultsOne: 'بالغ واحد',
            adultsMany: '{count} بالغين',
            childOne: 'طفل واحد',
            childrenMany: '{count} أطفال',
            infantOne: 'رضيع واحد',
            infantsMany: '{count} رضع',
            joiner: ' · '
        },
        pendingTripPreferenceSeparator: ' | ',
        errors: {
            SOMETHING_WENT_WRONG: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
            NETWORK_ERROR: 'تعذر الاتصال. يرجى التحقق من اتصالك والمحاولة مرة أخرى.',
            RATE_LIMITED: 'طلبات كثيرة جدًا. يرجى الانتظار قليلاً والمحاولة مرة أخرى.'
        },
        fallbacks: {
            clarificationDefault: 'يرجى مشاركة تفاصيل الرحلة الناقصة.',
            reviewTripPrompt: 'يرجى مراجعة تفاصيل رحلتك أدناه — هل ترغب في إجراء أي تعديل، أم أتابع؟',
            reviewTripQuickReplies: ['يلا نبدأ', 'حسنًا', 'تابع', 'ممتاز', 'استمر']
        },
        api: {
            askAboutFlights: 'يمكنني مساعدتك فقط في العثور على الرحلات المناسبة لك.',
            askAboutFlightsWithPending: 'يمكنني المساعدة في الرحلات فقط. ',
            greetingReply: 'مرحبًا! يمكنني مساعدتك في البحث عن الرحلات وتعديل تفاصيل الرحلة.',
            unrelatedReply: 'عذرًا، لا أستطيع المساعدة في هذا هنا. يمكنني المساعدة في تفاصيل البحث عن الرحلات.',
            tellRouteAndDate: 'أخبرني بمسارك وتاريخك، مثال: من بنغالور إلى دبي في 15 أبريل.',
            shareAirportsAndDate: 'يرجى مشاركة مطار المغادرة، مطار الوصول، وتاريخ المغادرة.',
            shareMoreTravellers: 'هل يمكنك إضافة بعض التفاصيل حتى نضبط عدد المسافرين بشكل صحيح؟',
            returnDateBeforeDepart: 'لا يمكن أن يكون تاريخ العودة قبل تاريخ المغادرة. يرجى مشاركة تاريخ عودة صحيح.',
            multiCityNotSupported:
                'الرحلات متعددة المدن غير مدعومة حتى الآن. يمكنني البحث عن رحلات ذهاب فقط أو ذهاب وعودة — يرجى تحديد مدينة مغادرة ووجهة واحدة.',
            questions: {
                from: 'ما هي مدينة أو مطار المغادرة؟',
                to: 'ما هي مدينة أو مطار الوصول؟',
                passengers:
                    'كم عدد المسافرين؟ البالغون 11+؛ الأطفال 2–11؛ الرضع أقل من عامين (كل رضيع يحتاج إلى بالغ مرافق).',
                departDate: 'ما هو تاريخ المغادرة؟ (مثال: 15 أبريل)',
                returnDate: 'ما هو تاريخ العودة؟ (مثال: 22 أبريل)'
            },
            passengerValidation: {
                adultsRange: 'كم عدد البالغين المسافرين؟ اختر من 1 إلى 9 (كل بالغ يجب أن يكون عمره 11 سنة أو أكثر).',
                noSeatsForChildren:
                    'مع تسعة بالغين، لا تتبقى مقاعد للأطفال في هذا الحجز. كم عدد البالغين والأطفال الذي تريد استخدامه؟',
                childrenAsk: (maxChildren: number, adults: number) =>
                    `كم عدد الأطفال من 2 إلى 11 سنة؟ يمكنك إضافة حتى ${maxChildren} ${maxChildren === 1 ? 'طفل' : 'أطفال'} مع ${adults} ${adults === 1 ? 'بالغ' : 'بالغين'}.`,
                infantsExceedAdults:
                    'كم عدد الرضع (أقل من عامين)؟ لا يمكن أن يتجاوز عدد الرضع عدد البالغين (كل رضيع يسافر مع بالغ واحد).'
            }
        }
    }
}

export type HomeAILocale = keyof typeof homeAIMessages

export function resolveHomeAILocale (locale?: string): HomeAILocale {
    return locale === 'ar' ? 'ar' : 'en'
}
